/**
 * AmbientWorldSetup — Register all ambient creatures + events for a world.
 *
 * Called once per scene create() to install the full ambient pass:
 *   - Creatures specific to the world (fish/seagulls/flying fish/etc)
 *   - Ambient audio loops
 *   - Random events (treasure, shooting star)
 *
 * Usage:
 *   AmbientWorldSetup.install(scene, worldNum);      // for GameScene
 *   AmbientWorldSetup.installBoss(scene);            // for BossScene
 */

import Phaser from 'phaser';
import { AmbientLife } from './AmbientLife';
import { AudioManager } from './AudioManager';

export class AmbientWorldSetup {
  /**
   * Install the full ambient pass for a game world.
   * @param scene    Phaser scene
   * @param worldNum 1=underwater coral, 2=beach, 3=ship/open sea
   */
  static install(scene: Phaser.Scene, worldNum: number): void {
    // Floating ambient (fish, seagulls, butterflies, sailboats, flying fish,
    // shooting stars, message-in-a-bottle) is reserved for World 1 — the
    // underwater Deep stages 1+2. Worlds 2+ ship without ambient per
    // design decision 2026-05-18. Audio loop still plays per world.
    if (worldNum === 1) {
      const life = AmbientLife.get(scene);
      this.ensureFishAnimations(scene);
      this.ensureGenericAnims(scene);
      this.installWorld1(life);
      this.installRandomEvents(life, worldNum);
    }

    this.startAmbientLoop(scene, worldNum);
  }

  /**
   * Boss arena — only the tension audio loop, no ambient floating debris
   * or toxic bubbles. Cleared 2026-05-18.
   */
  static installBoss(scene: Phaser.Scene): void {
    this.startAmbientLoop(scene, 4);
  }

  // ─── World-specific setups ────────────────────────────────────────────────

  private static installWorld1(life: AmbientLife): void {
    // Big fish (existing)
    life.register({
      tag: 'w1_fish',
      textureKeys: ['fish_bream_1', 'fish_mullet_1', 'fish_seabass_1', 'fish_sardine_1', 'fish_grouper_1']
        .filter(k => life['scene'].textures.exists(k)),
      animKey: 'fish_bream_swim', // Picked first available
      behavior: 'swimAcross',
      spawnInterval: { min: 5000, max: 11000 },
      maxConcurrent: 3,
      scale: { min: 0.06, max: 0.1 },
      alpha: { min: 1.0, max: 1.0 },
      depth: 7,
      yRange: { min: 160, max: 594 },
      initialDelay: 2000,
    });

    // Shoals of tiny fish
    life.register({
      tag: 'w1_shoal',
      textureKeys: ['ambient_school_tiny'].filter(k => life['scene'].textures.exists(k)),
      behavior: 'shoalSwim',
      spawnInterval: { min: 12000, max: 20000 },
      maxConcurrent: 12,
      scale: { min: 0.4, max: 0.8 },
      alpha: { min: 1.0, max: 1.0 },
      depth: 6,
      yRange: { min: 250, max: 650 },
      initialDelay: 8000,
      extras: { count: 10 },
    });

    // Medusa drifters — 32-frame Grok img2vid cycle (bell pulses, tentacles
    // trail). All spawners share the same animation; the legacy A/B static
    // variants were retired with the new client-supplied medusa reference.
    life.register({
      tag: 'w1_jellyfish',
      textureKeys: Array.from({ length: 32 }, (_, i) => `ambient_jellyfish_${i + 1}`)
        .filter(k => life['scene'].textures.exists(k)),
      animKey: 'jellyfish_drift',
      behavior: 'driftFloat',
      spawnInterval: { min: 18000, max: 28000 },
      maxConcurrent: 2,
      scale: { min: 0.5, max: 0.8 },
      alpha: { min: 0.7, max: 0.9 },
      depth: 5,
      initialDelay: 10000,
    });

    // Crab — stationary watcher on the ground, observing the fight.
    // 32-frame idle cycle (claws wave, body bob, antennae wiggle) generated
    // via Grok img2vid from the client-supplied crab reference. Pinned to
    // the clear sandy patch in front of the right-side coral cluster, far
    // enough left of the foreground seaweed so the silhouette reads cleanly.
    // Long spawnInterval because it stays forever once spawned.
    //
    // depth = -1 so Poseidon (default depth 0) renders IN FRONT of the
    // crab whenever they overlap — keeps the crab as a seafloor "decor"
    // rather than something the player can walk through.
    life.register({
      tag: 'w1_crab',
      textureKeys: Array.from({ length: 32 }, (_, i) => `ambient_crab_${i + 1}`)
        .filter(k => life['scene'].textures.exists(k)),
      animKey: 'crab_watch',
      behavior: 'stationaryWatcher',
      spawnInterval: { min: 999999, max: 999999 }, // never respawn
      maxConcurrent: 1,
      scale: { min: 0.238, max: 0.252 },
      alpha: { min: 1.0, max: 1.0 },
      depth: -1,
      initialDelay: 3000,
      extras: { x: 1240, y: 920 },
    });

    // Sea turtle — animated swim cycle drifting across the scene.
    // Spawned by stages whose educational.ambientCreature targets 'turtle'.
    // Gated by texture existence so stages without the assets skip silently.
    //
    // `spriteBaseFacing: 'right'` because the client-supplied turtle (and
    // the FlowNode gpt-image-2 sprite-sheet cycle generated from it) face
    // right — AmbientLife uses this hint to flip correctly per direction.
    //
    // spawnInterval is tight (2.5–4.5s) so a new turtle is always entering
    // as the last one exits. Combined with the 4–7s swim duration, there
    // is essentially always at least one turtle visible on screen — the
    // hero of the World 1 educational story should never be "off camera".
    //
    // scale is ~20% larger than fish/jellyfish so the turtle reads as the
    // primary ambient character rather than background chorus.
    life.register({
      tag: 'w1_turtle',
      textureKeys: ['ambient_turtle_1'].filter(k => life['scene'].textures.exists(k)),
      animKey: 'turtle_swim',
      behavior: 'swimAcross',
      // One turtle on screen at a time — it's the educational ambassador,
      // not a school. spawnInterval is short enough that the screen is
      // rarely empty for long after one exits.
      spawnInterval: { min: 1500, max: 3500 },
      maxConcurrent: 1,
      // Bigger than fish — the educational ambassador deserves presence.
      scale: { min: 0.30, max: 0.40 },
      // Fully opaque — the client turtle is the educational hero and must
      // read as a solid character, not a faded ambient drifter.
      alpha: { min: 1.0, max: 1.0 },
      depth: 8,
      // Upper-half spawn band only — the seabed weed layer lives below
      // ~Y=780 and turtles rendering in front of weeds break the
      // foreground/background illusion. Combined with the upward
      // yWanderRange + slow bob below, the turtle stays clear of the
      // bottom 30% of the screen at all times.
      yRange: { min: 200, max: 450 },
      initialDelay: 0,
      spriteBaseFacing: 'right',
      // Slow, calm crossing — bumped from 6.2–10.8s to 12–20s so the
      // turtle drifts gracefully across instead of zipping past.
      durationRange: { min: 12000, max: 20000 },
      // Mostly linear cross — small upward bias (so the turtle finishes a
      // little higher than it started) but no big diagonal lurch.
      yWanderRange: { min: -60, max: 20 },
      // Tiny slow-bob amplitude — just a hint of current drift on top of
      // the natural flipper-stroke bob, so the turtle reads as cruising
      // in a near-straight line rather than undulating up-and-down.
      slowBobAmpRange: { min: 10, max: 22 },
    });

    // Blue tang — 32-frame Grok img2vid swim cycle from the client-supplied
    // tang reference. Body undulates, tail beats, fins flutter; deterministic
    // chroma-key + union-bbox crop keeps the body anchored across frames so
    // there's zero flicker (see scripts/generate-fish-video-frames.py).
    //
    // Slots in alongside the legacy `w1_fish` school as a more featured
    // mid-foreground fish — bigger, fully opaque, fewer concurrent. The
    // tang faces RIGHT in the source art so spriteBaseFacing matches.
    life.register({
      tag: 'w1_tang',
      textureKeys: Array.from({ length: 32 }, (_, i) => `ambient_tang_${i + 1}`)
        .filter(k => life['scene'].textures.exists(k)),
      animKey: 'tang_swim',
      behavior: 'swimAcross',
      spawnInterval: { min: 7000, max: 14000 },
      maxConcurrent: 2,
      scale: { min: 0.20, max: 0.30 },
      alpha: { min: 1.0, max: 1.0 },
      depth: 7,
      yRange: { min: 220, max: 560 },
      initialDelay: 4000,
      spriteBaseFacing: 'right',
      durationRange: { min: 8000, max: 14000 },
      yWanderRange: { min: -40, max: 40 },
      slowBobAmpRange: { min: 6, max: 14 },
    });
  }

  // ─── Random events (shared across worlds) ────────────────────────────────

  private static installRandomEvents(life: AmbientLife, worldNum: number): void {
    // Shooting star removed — a star falling through the ocean read as out of
    // place underwater (World 1). Reinstate here if a sky/surface world wants it.

    // Message in a bottle (World 2+ only — beach/ship)
    if (worldNum >= 2) {
      life.register({
        tag: 'event_bottle',
        textureKeys: ['event_message_bottle'].filter(k => life['scene'].textures.exists(k)),
        behavior: 'debrisDrift',
        spawnInterval: { min: 40000, max: 70000 },
        maxConcurrent: 1,
        scale: { min: 0.35, max: 0.45 },
        alpha: { min: 0.85, max: 1.0 },
        depth: 7,
        yRange: { min: 850, max: 960 },
        initialDelay: 20000,
      });
    }
  }

  // ─── Audio loops ──────────────────────────────────────────────────────────

  private static startAmbientLoop(scene: Phaser.Scene, worldNum: number): void {
    // worldNum 1/2/3 → underwater, 4 → boss tension
    const key = worldNum === 4 ? 'amb_tension_loop' : 'amb_underwater_loop';
    if (!scene.cache.audio.exists(key)) return;

    try {
      const audio = AudioManager.getInstance();
      const mixerVol = audio.getMixerSettings().sfx.volume;
      const loopVolume = worldNum === 4 ? 0.25 : 0.15;
      const sound = scene.sound.add(key, {
        loop: true,
        volume: mixerVol * loopVolume,
      });
      sound.play();

      // Cleanup on shutdown
      scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        try { sound.stop(); sound.destroy(); } catch {}
      });
    } catch (e) {
      console.warn(`AmbientWorldSetup: failed to start loop ${key}`, e);
    }
  }

  // ─── Animation creation helpers ──────────────────────────────────────────

  private static ensureFishAnimations(scene: Phaser.Scene): void {
    const fishTypes = ['bream', 'mullet', 'seabass', 'sardine', 'grouper'];
    for (const type of fishTypes) {
      const animKey = `fish_${type}_swim`;
      if (scene.anims.exists(animKey)) continue;
      const frames: Phaser.Types.Animations.AnimationFrame[] = [];
      for (let i = 1; i <= 4; i++) {
        const k = `fish_${type}_${i}`;
        if (scene.textures.exists(k)) frames.push({ key: k });
      }
      if (frames.length >= 2) {
        scene.anims.create({ key: animKey, frames, frameRate: 6, repeat: -1, yoyo: true });
      }
    }
  }

  private static ensureGenericAnims(scene: Phaser.Scene): void {
    const makeAnim = (key: string, frameKeys: string[], fps: number, yoyo = true) => {
      if (scene.anims.exists(key)) return;
      const frames = frameKeys.filter(k => scene.textures.exists(k)).map(k => ({ key: k }));
      if (frames.length >= 2) {
        scene.anims.create({ key, frames, frameRate: fps, repeat: -1, yoyo });
      }
    };

    // Seagull flap
    makeAnim('seagull_flap', [
      'ambient_seagull_1',
      'ambient_seagull_4', // new mid-extended frame
      'ambient_seagull_2',
      'ambient_seagull_3',
    ], 8);

    // Medusa drift cycle — 32 frames extracted at 16fps from a 2s Grok
    // img2vid loop (Method A in docs/SPRITE_PIPELINE.md). Frames are
    // bell-top anchored in post (see scripts/generate-medusa-video-frames.py
    // fit_all_to_common_canvas) so the bell stays put across the cycle while
    // tentacles dangle freely below. Played at 16fps in-game → 2-second loop.
    const medusaFrames: string[] = [];
    for (let i = 1; i <= 32; i++) medusaFrames.push(`ambient_jellyfish_${i}`);
    makeAnim('jellyfish_drift', medusaFrames, 16, false);

    // Blue tang in-place swim cycle — 32 frames @ 16fps from the same
    // Grok img2vid pipeline as the medusa (see docs/SPRITE_PIPELINE.md
    // Method A + scripts/generate-fish-video-frames.py). Body undulates,
    // tail beats, fins flutter. 2-second seamless loop, no yoyo.
    const tangFrames: string[] = [];
    for (let i = 1; i <= 32; i++) tangFrames.push(`ambient_tang_${i}`);
    makeAnim('tang_swim', tangFrames, 16, false);

    // Sea turtle swim cycle — 32 frames extracted at 16fps from a 2s
    // FlowNode + Grok img2vid loop (see docs/SPRITE_PIPELINE.md "Method A").
    // 2x frame density (vs the prior 16-frame extraction) for smoother motion;
    // eyes + body morph naturally from the source video. The seed turtle was
    // intentionally drawn at 35% canvas height so flipper extensions never
    // touch the frame edges through the cycle. Played at 16fps in-game →
    // 2-second loop matching the source. No yoyo (cycle already loops).
    const turtleFrames: string[] = [];
    for (let i = 1; i <= 32; i++) turtleFrames.push(`ambient_turtle_${i}`);
    makeAnim('turtle_swim', turtleFrames, 16, false);

    // Plastic bag drift cycle — same pipeline as the turtle: 12-frame
    // seamless loop from Grok Imagine img2vid. Used as one of the item
    // variants inside bubbles flagged by stage.educational.itemTheme.
    const bagDriftFrames: string[] = [];
    for (let i = 1; i <= 12; i++) bagDriftFrames.push(`bag_drift_${i}`);
    makeAnim('bag_drift', bagDriftFrames, 7, false);

    // Butterfly wing flap
    makeAnim('butterfly_flap', [
      'ambient_butterfly',
      'ambient_butterfly_f2', // wings down
      'ambient_butterfly',
      'ambient_butterfly_f3', // wings closed
    ], 10);

    // Crab watching pose — 32-frame Grok img2vid idle cycle (claw wave,
    // body bob, antennae wiggle) generated from the client-supplied crab.
    // 16fps for the buttery-smooth motion the source video produced.
    makeAnim(
      'crab_watch',
      Array.from({ length: 32 }, (_, i) => `ambient_crab_${i + 1}`),
      16,
    );

    // Flying fish wing flap
    makeAnim('flyingfish_flap', [
      'ambient_flyingfish',
      'ambient_flyingfish_f2',
    ], 6);
  }
}
